import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { observer } from 'mobx-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';

import { toast } from 'react-toastify';
import { GetStaffPortalStore } from 'SessionState/Staff/StaffPortalStore';
import { useLiveTabulatorProgress } from 'hooks/useLiveTabulatorProgress';
import ReadOnlyLiveScoreFeed from './ReadOnlyLiveScoreFeed';
import { Document, Page, pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc =
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

import JudgeDetailsCard from './JudgeDetailsCard';
import DivisionContestantBrowser from './DivisionContestantBrowser';
import { ContestantRegistrationDetailsDTO, EmceeSheetContestantDTO, RegistrationInvoice } from 'API/Generated/data-contracts';
import EmceeSheetsModal from './EmceeSheetsModal';
import CsLineIcons from 'cs-line-icons/CsLineIcons';




type ReadOnlyStaffDashboardProps = {
  onLogout: () => void;
};



const ReadOnlyStaffDashboard =
  observer(
    ({
      onLogout,
    }: ReadOnlyStaffDashboardProps) => {
      const staffStore =
        GetStaffPortalStore();

      const currentTabulator =
        staffStore.CurrentTabulator;

      const dashboard =
        staffStore.ReadOnlyDashboard;
// =====================================================
      // RESULTS PDF
      // =====================================================
        
const [
  showResultsPdf,
  setShowResultsPdf,
] = useState<boolean>(false);

const [
  resultsPdfBlobUrl,
  setResultsPdfBlobUrl,
] = useState<string | null>(
  null,
);

const [
  isLoadingResultsPdf,
  setIsLoadingResultsPdf,
] = useState<boolean>(false);

const [
  resultsPdfError,
  setResultsPdfError,
] = useState<string | null>(
  null,
);

const [
  resultsPdfPageCount,
  setResultsPdfPageCount,
] = useState<number>(0);

const [
  resultsPdfWidth,
  setResultsPdfWidth,
] = useState<number>(800);

const resultsPdfContainerRef =
  useRef<HTMLDivElement | null>(
    null,
  );

  useEffect(() => {
  if (!showResultsPdf) {
    return;
  }

  const updatePdfWidth = () => {
    const container =
      resultsPdfContainerRef.current;

    if (!container) {
      return;
    }

    const availableWidth =
      container.clientWidth;

    setResultsPdfWidth(
      Math.max(
        280,
        Math.min(
          availableWidth - 24,
          1000,
        ),
      ),
    );
  };

  updatePdfWidth();

  const observer =
    new ResizeObserver(
      updatePdfWidth,
    );

  if (
    resultsPdfContainerRef.current
  ) {
    observer.observe(
      resultsPdfContainerRef.current,
    );
  }

  window.addEventListener(
    'orientationchange',
    updatePdfWidth,
  );

  window.addEventListener(
    'resize',
    updatePdfWidth,
  );

  return () => {
    observer.disconnect();

    window.removeEventListener(
      'orientationchange',
      updatePdfWidth,
    );

    window.removeEventListener(
      'resize',
      updatePdfWidth,
    );
  };
}, [
  showResultsPdf,
]);

useEffect(() => {
  return () => {
    if (
      resultsPdfBlobUrl
    ) {
      URL.revokeObjectURL(
        resultsPdfBlobUrl,
      );
    }
  };
}, [
  resultsPdfBlobUrl,
]);

const handleViewResultsPdf =
  async (): Promise<void> => {
    const pageantId =
      dashboard?.pageantId;

    if (!pageantId) {
      console.error(
        '[Results PDF] Missing pageantId.',
      );

      return;
    }

    // Open the modal FIRST so the user
    // immediately sees something happen.
    setShowResultsPdf(
      true,
    );

    setIsLoadingResultsPdf(
      true,
    );

    setResultsPdfError(
      null,
    );

    try {
      const pdfUrl =
        `/TabulatorPortal/ResultsPdf/Pageant:${pageantId}`;

      console.log(
        '[Results PDF] Loading:',
        pdfUrl,
      );

      const response =
        await fetch(
          pdfUrl,
          {
            method:
              'GET',

            credentials:
              'include',

            headers: {
              Accept:
                'application/pdf',
            },
          },
        );

      console.log(
        '[Results PDF] Response:',
        {
          status:
            response.status,

          ok:
            response.ok,

          contentType:
            response.headers.get(
              'content-type',
            ),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Unable to load PDF. HTTP ${response.status}`,
        );
      }

      const pdfBlob =
        await response.blob();

      console.log(
        '[Results PDF] Blob:',
        {
          size:
            pdfBlob.size,

          type:
            pdfBlob.type,
        },
      );

      if (
        pdfBlob.size === 0
      ) {
        throw new Error(
          'The PDF response was empty.',
        );
      }

      // Remove the previous object URL,
      // if one exists.
      if (
        resultsPdfBlobUrl
      ) {
        URL.revokeObjectURL(
          resultsPdfBlobUrl,
        );
      }

      const blobUrl =
        URL.createObjectURL(
          new Blob(
            [
              pdfBlob,
            ],
            {
              type:
                'application/pdf',
            },
          ),
        );

      setResultsPdfBlobUrl(
        blobUrl,
      );
    } catch (error) {
      console.error(
        '[Results PDF] Failed to load:',
        error,
      );

      setResultsPdfError(
        'The Results PDF could not be loaded.',
      );
    } finally {
      setIsLoadingResultsPdf(
        false,
      );
    }
  };
      // =====================================================
      // LOCAL UI STATE
      // =====================================================

      const [
        highlightStatus,
        setHighlightStatus,
      ] =
        React.useState(false);

      const [
        highlightNote,
        setHighlightNote,
      ] =
        React.useState(false);

      const [
        selectedJudgeId,
        setSelectedJudgeId,
      ] =
        React.useState<number | null>(
          null,
        );

        const [
  showEmceeSheets,
  setShowEmceeSheets,
] =
  React.useState<boolean>(
    false,
  );

  const [
  completedSideAwardGroups,
  setCompletedSideAwardGroups,
] = React.useState<Set<string>>(
  new Set<string>(),
);

const getSideAwardGroupKey = (
  divisionId: number,
  ageGroupId?: number,
): string => {
  return ageGroupId != null
    ? `${divisionId}-${ageGroupId}`
    : `${divisionId}-division`;
};

const isSideAwardsComplete = (
  divisionId: number,
  ageGroupId?: number,
): boolean => {
  return completedSideAwardGroups.has(
    getSideAwardGroupKey(
      divisionId,
      ageGroupId,
    ),
  );
};

      const [
        showContestantsModal,
        setShowContestantsModal,
      ] =
        React.useState(false);

type ContestantBrowserHeader = {
  title: string;
  subtitle?: string;
  backLabel?: string;
  contestantCount?: number;
  backAction?: () => void;
};



const [
  contestantBrowserHeader,
  setContestantBrowserHeader,
] =
  React.useState<ContestantBrowserHeader>({
    title: 'Contestants',
  });

      // =====================================================
      // EVENT ID
      // =====================================================

      const eventId =
        dashboard
          ?.judges?.[0]
          ?.eventId ??
        0;

      // =====================================================
      // HELPERS
      // =====================================================

      const getEventStatusText = (
        status?: number,
      ): string => {
        switch (status) {
          case 0:
            return 'Not Started';

          case 1:
            return 'Judging In Progress';

          case 2:
            return 'Tabulation In Progress';

          case 3:
            return 'Results Are In';

          case 4:
            return 'Complete';

              case 5:
      return 'ADMIN PAUSE REQUEST!';

          default:
            return 'Unknown';
        }
      };

      const getEventStatusClass = (
        status?: number,
      ): string => {
        switch (status) {
          case 0:
            return 'text-muted';

          case 1:
            return 'text-primary';

          case 2:
            return 'text-warning';

          case 3:
            return 'text-info';

          case 4:
            return 'text-success';

 case 5:
      return 'text-danger';

          default:
            return 'text-muted';
        }
      };

      const getProgressTextClass = (
        progress: number,
      ): string => {
        if (progress >= 100) {
          return 'text-success';
        }

        if (progress > 0) {
          return 'text-primary';
        }

        return 'text-muted';
      };

      // =====================================================
      // LIVE SIGNALR
      // =====================================================

      useLiveTabulatorProgress({
        eventId,

        enabled:
          Boolean(eventId) &&
          staffStore.isReadOnly,

        // ===================================================
        // SCORE / PROGRESS CHANGED
        // ===================================================

        onProgressChanged:
          async (change) => {
            console.log(
              '🔥 READ ONLY TABULATOR PROGRESS CHANGED',
              change,
            );

            staffStore
              .setLastProgressChange(
                change,
              );

            // A judge saving a score means
            // they are currently online.
            staffStore
              .setJudgeLiveStatus({
                eventId:
                  change.eventId,

                judgeId:
                  change.judgeId,

                liveStatus:
                  'Online',

                isOnline:
                  true,

                updatedAt:
                  change.updatedAt,
              });

            if (
              !currentTabulator
                ?.pageantId
            ) {
              return;
            }

            // -----------------------------------------------
            // BEFORE completion count
            // -----------------------------------------------

            const beforeJudge =
              staffStore
                .ReadOnlyDashboard
                ?.judges
                ?.find(
                  (judge) =>
                    Number(
                      judge.eventJudgeId,
                    ) ===
                    Number(
                      change.judgeId,
                    ),
                );

            const beforeAoc =
              beforeJudge
                ?.areasOfCompetition
                ?.find(
                  (aocProgress) =>
                    Number(
                      aocProgress
                        .areaOfCompetition
                        ?.pageantAvailableEventId,
                    ) ===
                    Number(
                      change.pageantAvailableEventId,
                    ),
                );

            const completedBefore =
              Number(
                beforeAoc
                  ?.completedContestants ??
                  0,
              );

            // -----------------------------------------------
            // REFRESH DASHBOARD
            // -----------------------------------------------

            const refreshedDashboard =
              await staffStore
                .getReadOnlyDashboard(
                  currentTabulator
                    .pageantId,
                );

            if (
              !refreshedDashboard
            ) {
              return;
            }

            // -----------------------------------------------
            // AFTER completion count
            // -----------------------------------------------

            const afterJudge =
              refreshedDashboard
                .judges
                ?.find(
                  (judge) =>
                    Number(
                      judge.eventJudgeId,
                    ) ===
                    Number(
                      change.judgeId,
                    ),
                );

            const afterAoc =
              afterJudge
                ?.areasOfCompetition
                ?.find(
                  (aocProgress) =>
                    Number(
                      aocProgress
                        .areaOfCompetition
                        ?.pageantAvailableEventId,
                    ) ===
                    Number(
                      change.pageantAvailableEventId,
                    ),
                );

            const completedAfter =
              Number(
                afterAoc
                  ?.completedContestants ??
                  0,
              );

            // -----------------------------------------------
            // CONTESTANT COMPLETED BY JUDGE
            // -----------------------------------------------

            if (
              completedAfter >
              completedBefore
            ) {
              staffStore
                .setJudgeLastCompleted({
                  judgeId:
                    change.judgeId,

                  registeredPageantContestantId:
                    change
                      .registeredPageantContestantId,

                  pageantAvailableEventId:
                    change
                      .pageantAvailableEventId,

                  completedAt:
                    change.updatedAt,
                });
            }
          },

        // ===================================================
        // JUDGE STATUS
        // ===================================================

        onJudgeStatusChanged:
          (status) => {
            console.log(
              '🟢 JUDGE STATUS CHANGED',
              status,
            );

            staffStore
              .setJudgeLiveStatus(
                status,
              );
          },

        // ===================================================
        // JUDGE SCORING CONTEXT
        // ===================================================

        onJudgeScoringContextChanged:
          (context) => {
            console.log(
              '🟡 JUDGE SCORING CONTEXT CHANGED',
              context,
            );
          },

        // ===================================================
        // ONE JUDGE FINISHED CONTESTANT + AOC
        // ===================================================

        onJudgeContestantAocCompleted:
          (event) => {
            console.log(
              '📥 JUDGE COMPLETED CONTESTANT AOC',
              event,
            );

            staffStore
              .addLiveFeedItem({
                id:
                  `judge-${event.judgeId}-` +
                  `${event.pageantAvailableEventId}-` +
                  `${event.registeredPageantContestantId}-` +
                  `${event.completedAt}`,

                type:
                  'judge-complete',

                judgeId:
                  event.judgeId,

                judgeLabel:
                  event.judgeLabel,

                registeredPageantContestantId:
                  event
                    .registeredPageantContestantId,

                contestantNumber:
                  event.contestantNumber,

                contestantName:
                  event.contestantName,

                pageantAvailableEventId:
                  event
                    .pageantAvailableEventId,

                aocName:
                  event.aocName,

                completedAt:
                  event.completedAt,
              });

            staffStore
              .setJudgeLastCompleted({
                judgeId:
                  event.judgeId,

                registeredPageantContestantId:
                  event
                    .registeredPageantContestantId,

                pageantAvailableEventId:
                  event
                    .pageantAvailableEventId,

                contestantNumber:
                  event.contestantNumber,

                contestantName:
                  event.contestantName,

                aocName:
                  event.aocName,

                completedAt:
                  event.completedAt,
              });
          },

        // ===================================================
        // ALL JUDGES FINISHED CONTESTANT + AOC
        // ===================================================

        onContestantAocCompleted:
          (event) => {
            console.log(
              '✅ ALL JUDGES COMPLETED CONTESTANT AOC',
              event,
            );

            staffStore
              .addLiveFeedItem({
                id:
                  `complete-` +
                  `${event.pageantAvailableEventId}-` +
                  `${event.registeredPageantContestantId}-` +
                  `${event.completedAt}`,

                type:
                  'contestant-aoc-complete',

                registeredPageantContestantId:
                  event
                    .registeredPageantContestantId,

                contestantNumber:
                  event.contestantNumber,

                contestantName:
                  event.contestantName,

                pageantAvailableEventId:
                  event
                    .pageantAvailableEventId,

                aocName:
                  event.aocName,

                completedAt:
                  event.completedAt,
              });

            if (
              currentTabulator
                ?.pageantId
            ) {
              void staffStore
                .getReadOnlyDashboard(
                  currentTabulator
                    .pageantId,
                );
            }
          },

        // ===================================================
        // TEST SCORE
        // ===================================================

        onTestScoreReceived:
          async (event) => {
            console.log(
              '🚨 TEST SCORE RECEIVED',
              event,
            );

            staffStore
              .addLiveFeedItem({
                id:
                  `test-score-${event.judgeId}-` +
                  `${event.receivedAt}`,

                type:
                  'test-score',

                judgeId:
                  event.judgeId,

                judgeLabel:
                  event.judgeLabel,

                registeredPageantContestantId:
                  0,

                contestantName:
                  '',

                pageantAvailableEventId:
                  0,

                aocName:
                  '',

                completedAt:
                  event.receivedAt,
              });

            if (
              currentTabulator
                ?.pageantId
            ) {
              await staffStore
                .getReadOnlyDashboard(
                  currentTabulator
                    .pageantId,
                );
            }
          },

        // ===================================================
        // SIDE AWARDS COMPLETE
        // ===================================================

  //      onSideAwardsCompleted:
  // (event) => {
  //   console.log(
  //     '🏆 SIDE AWARDS COMPLETE',
  //     event,
  //   );

  //   setCompletedSideAwardGroups(
  //     (current) => {
  //       const next =
  //         new Set<string>(
  //           current,
  //         );

  //       next.add(
  //         getSideAwardGroupKey(
  //           event.divisionId,
  //           event.ageGroupId ??
  //             undefined,
  //         ),
  //       );

  //       return next;
  //     },
  //   );

  //           const groupLabel =
  //             event.ageGroupName
  //               ? `${event.divisionName} · ${event.ageGroupName}`
  //               : event.divisionName;

  //           staffStore
  //             .addLiveFeedItem({
  //               id:
  //                 `side-awards-${event.divisionId}-` +
  //                 `${
  //                   event.ageGroupId ??
  //                   'division'
  //                 }`,

  //               type:
  //                 'side-awards-complete',

  //               registeredPageantContestantId:
  //                 0,

  //               contestantName:
  //                 '',

  //               pageantAvailableEventId:
  //                 0,

  //               aocName:
  //                 'Side Awards',

  //               divisionId:
  //                 event.divisionId,

  //               divisionName:
  //                 event.divisionName,

  //               ageGroupId:
  //                 event.ageGroupId ??
  //                 undefined,

  //               ageGroupName:
  //                 event.ageGroupName ??
  //                 undefined,

  //               completedAt:
  //                 event.completedAt,
  //             });

  //           toast.success(
  //             `${groupLabel} COMPLETE with SIDE AWARDS`,
  //           );
  //         },

  onSideAwardsCompleted:
  (event) => {
    console.log(
      '🏆 SIDE AWARDS COMPLETE',
      event,
    );

    // =============================================
    // LIVE UPDATE THE EXISTING DASHBOARD
    // =============================================

    staffStore
      .updateSideAwardCompletion(
        event.divisionId,
        event.ageGroupId ??
          undefined,
      );

    // =============================================
    // DISPLAY LABEL
    // =============================================

    const groupLabel =
      event.ageGroupName
        ? `${event.divisionName} · ${event.ageGroupName}`
        : event.divisionName;

    // =============================================
    // LIVE FEED
    // =============================================

    staffStore
      .addLiveFeedItem({
        id:
          `side-awards-${event.divisionId}-` +
          `${
            event.ageGroupId ??
            'division'
          }`,

        type:
          'side-awards-complete',

        registeredPageantContestantId:
          0,

        contestantName:
          '',

        pageantAvailableEventId:
          0,

        aocName:
          'Side Awards',

        divisionId:
          event.divisionId,

        divisionName:
          event.divisionName,

        ageGroupId:
          event.ageGroupId ??
          undefined,

        ageGroupName:
          event.ageGroupName ??
          undefined,

        completedAt:
          event.completedAt,
      });

    // =============================================
    // NOTIFICATION
    // =============================================

    toast.success(
      `${groupLabel} COMPLETE with SIDE AWARDS`,
    );
  },

        // ===================================================
        // DASHBOARD UPDATED
        // ===================================================

        onDashboardUpdated:
          async (event) => {
            console.log(
              '📣 DASHBOARD UPDATE RECEIVED',
              event,
            );

            if (
              !currentTabulator
                ?.pageantId
            ) {
              return;
            }

            const before =
              staffStore
                .ReadOnlyDashboard;

            const oldStatus =
              before?.eventStatus;

            const oldMessage =
              before
                ?.eventStatusMessage ??
              '';

              const oldPdfUrl =
  before?.resultsPdfUrl ??
  '';

const oldPdfUpdatedAt =
  before?.resultsPdfUpdatedAt ??
  '';

            const refreshedDashboard =
              await staffStore
                .getReadOnlyDashboard(
                  currentTabulator
                    .pageantId,
                );

            if (
              !refreshedDashboard
            ) {
              return;
            }

            const newStatus =
              refreshedDashboard
                .eventStatus;

            const newMessage =
              refreshedDashboard
                .eventStatusMessage ??
              '';

              const newPdfUrl =
  refreshedDashboard
    .resultsPdfUrl ??
  '';

const newPdfUpdatedAt =
  refreshedDashboard
    .resultsPdfUpdatedAt ??
  '';

  // -----------------------------------------------
// RESULTS PDF ADDED / UPDATED
// -----------------------------------------------

if (
  newPdfUrl &&
  (
    oldPdfUrl !== newPdfUrl ||
    oldPdfUpdatedAt !==
      newPdfUpdatedAt
  )
) {
  toast.success(
    oldPdfUrl
      ? 'Crowning Results PDF Updated!'
      : 'Crowning Results PDF is Available!',
  );
}

            // -----------------------------------------------
            // STATUS CHANGED
            // -----------------------------------------------

           if (
  oldStatus !==
  newStatus
) {
  if (
    Number(newStatus) === 5
  ) {
    toast.error(
      'ADMIN PAUSE REQUEST! Please touch base with the tabulator.',
      {
        autoClose: 8000,
      },
    );
  } else {
    toast.info(
      'Status Changed',
    );
  }

              setHighlightStatus(
                true,
              );

              window.setTimeout(
                () => {
                  setHighlightStatus(
                    false,
                  );
                },
                3000,
              );
            }

            // -----------------------------------------------
            // ADMIN NOTE CHANGED
            // -----------------------------------------------

            // -----------------------------------------------
// ADMIN NOTE CHANGED
// -----------------------------------------------

if (
  oldMessage !==
    newMessage &&
  newMessage
) {
  toast.info(
    'New Note from Admin!',
  );

  setHighlightNote(
    true,
  );

  window.setTimeout(
    () => {
      setHighlightNote(
        false,
      );
    },
    3000,
  );
}
          },
      });



const loadContestantDetails =
  React.useCallback(
    async (
      registeredContestantId: number,
    ): Promise<ContestantRegistrationDetailsDTO | null> => {
      const pageantId =
        staffStore.CurrentTabulator?.pageantId;

      if (!pageantId) {
        console.error(
          'Unable to load contestant details: missing pageantId.',
        );

        return null;
      }

      return staffStore
        .getContestantRegistrationDetails(
          pageantId,
          registeredContestantId,
        );
    },
    [
      staffStore,
      staffStore.CurrentTabulator?.pageantId,
    ],
  );

  const loadEmceeSheets =
  React.useCallback(
    async (
      divisionId?: number,
      ageGroupId?: number,
    ): Promise<EmceeSheetContestantDTO[] | null> => {
      const pageantId =
        staffStore
          .CurrentTabulator
          ?.pageantId;

      if (!pageantId) {
        console.error(
          'Unable to load emcee sheets: missing pageantId.',
        );

        return null;
      }

      return await staffStore
        .getEmceeSheets(
          pageantId,
          divisionId,
          ageGroupId,
        );
    },
    [
      staffStore,
      staffStore
        .CurrentTabulator
        ?.pageantId,
    ],
  );

      // =====================================================
      // INITIAL LOAD
      // =====================================================

      useEffect(() => {
        if (
          !currentTabulator
            ?.pageantId
        ) {
          return;
        }

        void staffStore
          .getReadOnlyDashboard(
            currentTabulator
              .pageantId,
          );
      }, [
        currentTabulator
          ?.pageantId,
      ]);

      // =====================================================
      // LOADING
      // =====================================================

      if (
        staffStore.loading &&
        !dashboard
      ) {
        return (
          <div className="container py-5 text-center">
            <Spinner
              animation="border"
            />

            <div className="mt-3">
              Loading event progress...
            </div>
          </div>
        );
      }

      // =====================================================
      // NO DATA
      // =====================================================

      if (!dashboard) {
        return (
          <div className="container py-5">
            <Card>
              <Card.Body className="text-center p-5">
                <h3>
                  Unable to Load Event
                </h3>

                <p className="text-muted mb-0">
                  No read-only dashboard
                  data was found.
                </p>
              </Card.Body>
            </Card>
          </div>
        );
      }

      const overallProgress =
        Number(
          dashboard
            .overallProgressPercent ??
          0,
        );

      // =====================================================
      // UI
      // =====================================================

      return (
        <div>
          <div className="pt-4">
            {/* =========================================== */}
            {/* EVENT NAME */}
            {/* =========================================== */}

            <div className="text-center mb-4">
              <h1 className="fw-bold mb-1">
                {dashboard.eventName ||
                  'Pageant'}
              </h1>

              {dashboard.eventDate && (
                <div className="text-muted">
                  {new Date(
                    dashboard.eventDate,
                  ).toLocaleDateString(
                    'en-US',
                    {
                      weekday:
                        'long',
                      month:
                        'long',
                      day:
                        'numeric',
                      year:
                        'numeric',
                    },
                  )}
                </div>
              )}
            </div>

            {/* =========================================== */}
            {/* EVENT UPDATE */}
            {/* =========================================== */}
{Number(dashboard.eventStatus) === 5 && (
  <Alert
    variant="danger"
    className="text-center mb-4 shadow-sm border border-danger border-3"
  >
    <div className="fs-3 fw-bold">
      ADMIN PAUSE REQUEST!
    </div>

    <div className="fs-5">
      Please pause the event and touch base with the tabulator.
    </div>

    {dashboard.eventStatusMessage && (
      <div className="mt-2 fw-semibold">
        {dashboard.eventStatusMessage}
      </div>
    )}
  </Alert>
)}
            {dashboard.eventStatusMessage && (
              <Alert
                variant="info"
                className={`text-center mb-4 shadow-sm ${
                  highlightNote
                    ? 'border border-warning border-3'
                    : 'border-0'
                }`}
              >
                <div className="fw-bold">
                  Event Update
                </div>

                <div>
                  {
                    dashboard
                      .eventStatusMessage
                  }
                </div>
              </Alert>
            )}

            {/* =========================================== */}
            {/* MAIN DASHBOARD ROW */}
            {/* =========================================== */}

            <Row>
              {/* ========================================= */}
              {/* LIVE FEED */}
              {/* ========================================= */}
{/* {dashboard.resultsPdfUrl && (
<Button
  variant="success"
  className="mb-3 p-3 text-large w-100"
  onClick={() => {
    const pageantId =
      dashboard.pageantId;

    if (!pageantId) {
      return;
    }

  window.open(
  `${window.location.origin}/TabulatorPortal/ResultsPdf/Pageant:${pageantId}`,
  '_blank',
  'noopener,noreferrer',
);
  }}
>
  VIEW RESULTS PDF
</Button>
)} */}
              <Col md={4}>
              <Button
  variant="primary"
  className="mb-3 p-3 text-large w-100"
  onClick={() => {
    setShowEmceeSheets(
      true,
    );
  }}
>
  EMCEE SHEETS
</Button>
                <ReadOnlyLiveScoreFeed
                  notifications={
                    staffStore
                      .LiveFeedItems
                  }
                />
              </Col>

              {/* ======================================= */}
{/* EMCEE SHEETS MODAL */}
{/* ======================================= */}

<EmceeSheetsModal
  show={
    showEmceeSheets
  }
  onHide={() => {
    setShowEmceeSheets(
      false,
    );
  }}
  divisions={
    dashboard.divisions ??
    []
  }
  loadEmceeSheets={
    loadEmceeSheets
  }
/>

              {/* ========================================= */}
              {/* DASHBOARD */}
              {/* ========================================= */}

              <Col md={8}>
                {/* ======================================= */}
                {/* TOP SUMMARY CARDS */}
                {/* ======================================= */}

                <Row className="g-3 mb-4 row-cols-2 row-cols-md-4">
                  {/* CONTESTANTS */}

                  <Col>
                    <Card
                      className="h-100 shadow-sm border-0"
                      role="button"
                      onClick={() => {
                        setShowContestantsModal(
                          true,
                        );
                      }}
                      style={{
                        cursor:
                          'pointer',
                      }}
                    >
                      <Card.Body className="text-center p-4">
                        <div className="text-muted fw-bold text-uppercase small">
                          Contestants
                        </div>

                        <div className="display-1 fw-bold mb-1">
                          {
                            dashboard
                              .totalContestants ??
                            0
                          }
                        </div>

                        <div className="small text-muted">
                          View Contestants
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* JUDGES */}

                  <Col>
                    <Card className="h-100 shadow-sm border-0">
                      <Card.Body className="text-center p-4">
                        <div className="text-muted fw-bold text-uppercase small">
                          Judges
                        </div>

                        <div className="display-1 fw-bold mb-1">
                          {
                            dashboard
                              .totalJudges ??
                            dashboard
                              .judges
                              ?.length ??
                            0
                          }
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* OVERALL PROGRESS */}

                  <Col>
                    <Card className="h-100 shadow-sm border-0">
                      <Card.Body className="text-center p-4">
                        <div className="text-muted fw-bold text-uppercase small">
                          Overall Progress
                        </div>

                        <div
                          className={`display-1 fw-bold mb-1 ${getProgressTextClass(
                            overallProgress,
                          )}`}
                        >
                          {overallProgress.toFixed(
                            0,
                          )}
                          %
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* EVENT STATUS */}

                 <Col>
  <Card
    className={`h-100 shadow-sm ${
      highlightStatus
        ? 'border border-warning border-3'
        : 'border-0'
    }`}
  >
    <Card.Body className="text-center p-4">
      <div className="text-muted fw-bold text-uppercase small">
        Event Status
      </div>

      <div
        className={`display-6 fw-bold mb-1 ${getEventStatusClass(
          dashboard.eventStatus,
        )}`}
      >
        {getEventStatusText(
          dashboard.eventStatus,
        )}
      </div>

      {Boolean(
        dashboard.resultsPdfUrl,
      ) &&
        Number(
          dashboard.eventStatus,
        ) === 3 && (
          <Button
            variant="info"
            className="p-2 mt-2 border-0 text-decoration-none"
            title="View Results PDF"
            aria-label="View Results PDF"
onClick={() => {
  void handleViewResultsPdf();
}}
          >
            <CsLineIcons
              icon="file-text"
              className="text-white cta-2"
              
            />
             

            <div className="small text-white mt-1">
              View Results PDF
            </div>
          </Button>
        )}
    </Card.Body>
  </Card>
</Col>

<Modal
  show={
    showResultsPdf
  }
  onHide={() => {
    setShowResultsPdf(
      false,
    );
  }}
  fullscreen="md-down"
  size="xl"
  centered
>
  <Modal.Header
    closeButton
    className="py-2 px-3"
  >
    <Modal.Title>
      Results
    </Modal.Title>
  </Modal.Header>

  <Modal.Body
    className="p-0"
    style={{
      height:
        'calc(100dvh - 120px)',

      minHeight:
        '400px',

      overflow:
        'hidden',
    }}
  >
    {isLoadingResultsPdf ? (
      <div className="h-100 d-flex flex-column align-items-center justify-content-center">
        <Spinner
          animation="border"
        />

        <div className="mt-3 text-muted">
          Loading Results...
        </div>
      </div>
    ) : resultsPdfError ? (
      <div className="p-5 text-center">
        <div className="text-danger fw-bold mb-3">
          {
            resultsPdfError
          }
        </div>

        <Button
          variant="primary"
          onClick={() => {
            void handleViewResultsPdf();
          }}
        >
          Try Again
        </Button>
      </div>
    ) : resultsPdfBlobUrl ? (
      <div
        ref={
          resultsPdfContainerRef
        }
        className="h-100 w-100"
        style={{
          overflowY:
            'auto',

          overflowX:
            'hidden',

          WebkitOverflowScrolling:
            'touch',

          overscrollBehavior:
            'contain',
        }}
      >
        <Document
          file={
            resultsPdfBlobUrl
          }
          loading={
            <div className="p-5 text-center">
              <Spinner
                animation="border"
              />

              <div className="mt-3 text-muted">
                Preparing Results...
              </div>
            </div>
          }
          error={
            <div className="p-5 text-center text-danger">
              Unable to display
              Results PDF.
            </div>
          }
          onLoadSuccess={(
            pdf,
          ) => {
            setResultsPdfPageCount(
              pdf.numPages,
            );
          }}
          onLoadError={(
            error,
          ) => {
            console.error(
              '[Results PDF] Render error:',
              error,
            );

            setResultsPdfError(
              'The Results PDF could not be displayed.',
            );
          }}
        >
          <div
            className="d-flex flex-column align-items-center py-3"
            style={{
              gap:
                '16px',
            }}
          >
            {Array.from(
              {
                length:
                  resultsPdfPageCount,
              },
              (
                _,
                index,
              ) => {
                const pageNumber =
                  index + 1;

                return (
                  <div
                    key={
                      pageNumber
                    }
                    className="bg-white shadow-sm"
                    style={{
                      maxWidth:
                        '100%',
                    }}
                  >
                    <Page
                      pageNumber={
                        pageNumber
                      }
                      width={
                        resultsPdfWidth
                      }
                      renderAnnotationLayer={
                        false
                      }
                      renderTextLayer={
                        false
                      }
                      loading={
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width:
                              resultsPdfWidth,

                            minHeight:
                              '300px',
                          }}
                        >
                          <Spinner
                            animation="border"
                            size="sm"
                          />
                        </div>
                      }
                    />

                    {resultsPdfPageCount >
                      1 && (
                      <div className="small text-muted text-center py-2 border-top">
                        Page{' '}
                        {
                          pageNumber
                        }{' '}
                        of{' '}
                        {
                          resultsPdfPageCount
                        }
                      </div>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </Document>
      </div>
    ) : (
      <div className="h-100 d-flex align-items-center justify-content-center text-muted">
        PDF unavailable.
      </div>
    )}
  </Modal.Body>

  <Modal.Footer className="py-2">
    {resultsPdfBlobUrl && (
      <Button
        variant="outline-primary"
        onClick={() => {
          window.open(
            resultsPdfBlobUrl,
            '_blank',
          );
        }}
      >
        Open PDF
      </Button>
    )}

    <Button
      variant="secondary"
      onClick={() => {
        setShowResultsPdf(
          false,
        );
      }}
    >
      Close
    </Button>
  </Modal.Footer>
</Modal>
                </Row>

                {/* ======================================= */}
                {/* CONTESTANT MODAL */}
                {/* ======================================= */}

                <Modal
                  show={
                    showContestantsModal
                  }
                  onHide={() => {
                    setShowContestantsModal(
                      false,
                    );
                  }}
                  fullscreen
                >
<Modal.Header
  closeButton
  className="py-3 px-4"
>
  <div className="d-flex align-items-center justify-content-between w-100 me-3">
    <div className="d-flex align-items-center gap-3">
      {contestantBrowserHeader.backAction && (
        <Button
          variant="outline-primary"
          onClick={
            contestantBrowserHeader.backAction
          }
          aria-label="Go back"
          title="Go back"
        >
          <CsLineIcons
            icon="chevron-left"
          />
        </Button>
      )}

      <Modal.Title className="fs-3 fw-bold mb-0">
        {
          contestantBrowserHeader.title
        }
      </Modal.Title>
    </div>

    {contestantBrowserHeader.contestantCount !==
      undefined && (
      <div className="fs-4 fw-semibold text-muted">
        {
          contestantBrowserHeader.contestantCount
        }{' '}
        {contestantBrowserHeader
          .contestantCount === 1
          ? 'contestant'
          : 'contestants'}
      </div>
    )}
  </div>
</Modal.Header>

<Modal.Body className="p-4">
<DivisionContestantBrowser
  divisions={
    dashboard.divisions ?? []
  }
  onHeaderChange={
    setContestantBrowserHeader
  }
loadContestantDetails={
    loadContestantDetails
  }
/>
</Modal.Body>
                </Modal>

                {/* ======================================= */}
                {/* JUDGE SUMMARY */}
                {/* ======================================= */}

                <hr />

                <div className="fw-bold small mb-2 text-center">
                  Judges
                </div>

                {/* <Row className="g-2 mb-4 row-cols-2 row-cols-md-4">
                  {dashboard
                    .judges
                    ?.map(
                      (judge) => {
                        const judgeId =
                          Number(
                            judge
                              .eventJudgeId ??
                            0,
                          );

                        const progress =
                          Number(
                            judge
                              .progressPercent ??
                            0,
                          );

                        const liveStatus =
                          tabulatorStore
                            .JudgeLiveStatuses[
                            judgeId
                          ];

                        const isOnline =
                          liveStatus
                            ?.isOnline ===
                            true ||
                          liveStatus
                            ?.liveStatus ===
                            'Online' ||
                          liveStatus
                            ?.liveStatus ===
                            'Scoring' ||
                          liveStatus
                            ?.liveStatus ===
                            'LoggedIn';

                        return (
                          <Col
                            key={
                              judge.eventJudgeId
                            }
                          >
                            <div
                              className="card rounded p-3 h-100 text-center"
                              role="button"
                              onClick={() => {
                                setSelectedJudgeId(
                                  judgeId,
                                );
                              }}
                              style={{
                                cursor:
                                  'pointer',
                              }}
                            >
                              <div
                                className={`display-2 fw-bold mb-1 ${getProgressTextClass(
                                  progress,
                                )}`}
                              >
                                {progress.toFixed(
                                  0,
                                )}
                                %
                              </div>

                              <div className="small fw-bold">
                                {judge.judgeLabel ||
                                  'Judge'}
                              </div>

                              <div className="mt-2 d-flex flex-column align-items-center gap-1">
                                {isOnline && (
                                  <Badge bg="success">
                                    Online
                                  </Badge>
                                )}

                                {judge.hasTestScore && (
                                  <Badge bg="danger">
                                    TEST COMPLETE
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </Col>
                        );
                      },
                    )}
                </Row> */}

                <Row className="g-3 mb-4 row-cols-2 row-cols-md-4">
  {dashboard
    .judges
    ?.map((judge) => {
      const judgeId =
        Number(
          judge.eventJudgeId ??
            0,
        );

      const progress =
        Number(
          judge.progressPercent ??
            0,
        );

      const safeProgress =
        Math.min(
          100,
          Math.max(
            0,
            progress,
          ),
        );

      const liveStatus =
        staffStore
          .JudgeLiveStatuses[
          judgeId
        ];

      const isOnline =
        liveStatus
          ?.isOnline ===
          true ||
        liveStatus
          ?.liveStatus ===
          'Online' ||
        liveStatus
          ?.liveStatus ===
          'Scoring' ||
        liveStatus
          ?.liveStatus ===
          'LoggedIn';

const progressColor =
  safeProgress >= 100
    ? 'var(--success)'
    : safeProgress > 0
      ? 'var(--warning)'
      : '#adb5bd';

const trackColor =
  safeProgress === 0
    ? '#adb5bd'
    : '#e9ecef';
    
      return (
        <Col
          key={
            judge.eventJudgeId
          }
        >
          <div
            className="card rounded p-3 h-100 text-center"
            role="button"
            onClick={() => {
              setSelectedJudgeId(
                judgeId,
              );
            }}
            style={{
              cursor: 'pointer',
            }}
          >
            {/* CIRCLE PROGRESS */}

            <div className="d-flex justify-content-center mb-3">
              <div
                className="position-relative d-flex align-items-center justify-content-center"
                style={{
                  width: '135px',
                  height: '135px',
                  borderRadius:
                    '50%',
background: `conic-gradient(
  ${progressColor} ${safeProgress}%,
  ${trackColor} ${safeProgress}% 100%
)`,
                }}
              >
                <div
                  className="position-absolute bg-background d-flex align-items-center justify-content-center"
                  style={{
                    width: '90px',
                    height: '90px',
                    borderRadius:
                      '50%',
                  }}
                >
                  <div
                    className={`fs-2 fw-bold `}
                  >
                    {progress.toFixed(
                      0,
                    )}
                    %
                  </div>
                </div>
              </div>
            </div>

            {/* JUDGE LABEL */}

            <div className="fw-bold">
              {judge.judgeLabel ||
                'Judge'}
            </div>

            {/* STATUS BADGES */}

            <div className="mt-2 d-flex flex-column align-items-center gap-1">
              {isOnline && (
                <Badge
                  bg="success"
                  className="fs-6"
                >
                  Online
                </Badge>
              )}

              {judge.hasTestScore && (
                <Badge
                  bg="danger"
                  className="fs-6"
                >
                  TEST COMPLETE
                </Badge>
              )}
            </div>
          </div>
        </Col>
      );
    })}
</Row>

                {/* ======================================= */}
                {/* JUDGE DETAILS SLIDE OUT */}
                {/* ======================================= */}

                <JudgeDetailsCard
                  judgeId={
                    selectedJudgeId
                  }
                  onClose={() => {
                    setSelectedJudgeId(
                      null,
                    );
                  }}
                />

                {/* ======================================= */}
                {/* AOC PROGRESS */}
                {/* ======================================= */}

                <hr />

                <div className="fw-bold small mb-2 text-center">
                  Areas of Competition
                </div>

                <Row className="g-2 mb-4 row-cols-2 row-cols-md-4">
                  {dashboard
                    .areasOfCompetition
                    ?.map(
                      (
                        aocProgress,
                      ) => {
                        const aoc =
                          aocProgress
                            .areaOfCompetition;

                        if (!aoc) {
                          return null;
                        }

                        const progress =
                          Number(
                            aocProgress
                              .progressPercent ??
                            0,
                          );

                        return (
                          <Col
                            key={
                              aoc
                                .pageantAvailableEventId
                            }
                          >
                            <div className="card rounded p-3 h-100 text-center">
                              <div
                                className={`display-6 fw-bold mb-1 ${getProgressTextClass(
                                  progress,
                                )}`}
                              >
                                {progress.toFixed(
                                  0,
                                )}
                                %
                              </div>

                              <div className="small fw-bold text-dark">
                                {
                                  aoc.aocName
                                }
                              </div>
                            </div>
                          </Col>
                        );
                      },
                    )}
                </Row>
              </Col>
            </Row>
          </div>

         
        </div>
      );
    },
  );

export default ReadOnlyStaffDashboard;